import React from "react";
import { AlertCircle } from "lucide-react";
import logger from "../utils/logger";


class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this.timeoutId = null;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidMount() {
    window.addEventListener("error", this.handleGlobalError);
    window.addEventListener("unhandledrejection", this.handleGlobalRejection);

    // Safety check: if maps doesn't load after 8s, trigger error fallback
    this.timeoutId = setTimeout(() => {
      // Check if any maps are successfully loaded or if we already have an error
      const mapsScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (mapsScript && !window.google?.maps && !this.state.hasError) {
        logger.warn("MapErrorBoundary: Google Maps script found but object failed to initialize in time.");
        this.triggerError("Map initialization timeout");
      }
    }, 8000);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this.handleGlobalError);
    window.removeEventListener("unhandledrejection", this.handleGlobalRejection);
    if (this.timeoutId) clearTimeout(this.timeoutId);
  }

  handleGlobalError = event => {
    const errorMsg = event.message || "";
    const stack = (event.error && event.error.stack) || "";
    this.checkIfMapError(errorMsg, stack);
  };

  handleGlobalRejection = event => {
    const reason = event.reason || {};
    const errorMsg = reason.message || "";
    const stack = reason.stack || "";
    this.checkIfMapError(errorMsg, stack);
  };

  checkIfMapError = (msg, stack) => {
    const errorStr = (msg + " " + stack).toLowerCase();
    const isMapError =
      errorStr.includes("maps.googleapis.com") ||
      errorStr.includes(".hj") ||
      errorStr.includes("intersectionobserver") ||
      errorStr.includes("places has been provided more than once");

    if (isMapError && !this.state.hasError) {
      logger.error("MapErrorBoundary caught global map error:", msg);
      this.triggerError(msg);
    }
  };

  triggerError = msg => {
    this.setState({ hasError: true });
    if (this.props.onError) {
      this.props.onError(new Error(msg));
    }
  };

  componentDidCatch(error, errorInfo) {
    logger.error("MapErrorBoundary caught an error:", error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-gray-50 border border-gray-100 rounded-xl animate-in fade-in duration-500">
          <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <h3 className="text-base font-bold text-gray-900 mb-1">Map Component Failed</h3>
          <p className="text-sm text-gray-500 max-w-xs">
            Google Maps encountered a runtime error. Don't worry, you can still enter the address manually below.
          </p>
          <button
            onClick={() => this.setState({ hasError: false })}
            className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            Try reloading map
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default MapErrorBoundary;
